import type { ReactNode } from 'react';
import React, { createContext, useContext } from 'react';
import { useMobileDetection } from '../hooks/useMobileDetection';

interface MobileContextType {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
  orientation: 'portrait' | 'landscape';
  deviceType: 'mobile' | 'tablet' | 'desktop';
  isTouchDevice: boolean;
  getResponsiveClasses: (mobile: string, tablet?: string, desktop?: string) => string;
  getMobileClasses: (mobile: string, desktop?: string) => string;
  getDesktopClasses: (desktop: string, mobile?: string) => string;
}

const MobileContext = createContext<MobileContextType | undefined>(undefined);

export const useMobile = (): MobileContextType => {
  const context = useContext(MobileContext);
  if (context === undefined) {
    throw new Error('useMobile must be used within a MobileProvider');
  }
  return context;
};

interface MobileProviderProps {
  children: ReactNode;
}

export const MobileProvider: React.FC<MobileProviderProps> = ({ children }) => {
  const mobileDetection = useMobileDetection();

  const getResponsiveClasses = (
    mobile: string,
    tablet: string = mobile,
    desktop: string = tablet
  ): string => {
    return `${mobile} md:${tablet} lg:${desktop}`;
  };

  const getMobileClasses = (mobile: string, desktop: string = ''): string => {
    return `${mobile} lg:${desktop}`;
  };

  const getDesktopClasses = (desktop: string, mobile: string = ''): string => {
    return `${mobile} lg:${desktop}`;
  };

  const value: MobileContextType = {
    ...mobileDetection,
    getResponsiveClasses,
    getMobileClasses,
    getDesktopClasses,
  };

  return (
    <MobileContext.Provider value={value}>
      {children}
    </MobileContext.Provider>
  );
};
